// e1c/src/pages/PatientDetail.js
// Author: Sunil Jain
// MODIFIED: Standardized API URL prefixes to /api/ and fixed fetchPatientDirectives

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInDays, format, isValid, parseISO } from 'date-fns';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
    Alert, Box, Button, Card, CardActions, CardContent, Chip, CircularProgress, Container, Dialog,
    DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton, InputLabel,
    List, ListItem, ListItemText, MenuItem, Paper, Select, Snackbar, Stack, TextField, Tooltip,
    Typography, useTheme
} from '@mui/material';

import { alpha } from '@mui/material/styles';

// Material-UI Icons
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LinkIcon from '@mui/icons-material/Link';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MedicationIcon from '@mui/icons-material/Medication'; 
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LaunchIcon from '@mui/icons-material/Launch'; 
import TrendingUpIcon from '@mui/icons-material/TrendingUp'; 
import PsychologyIcon from '@mui/icons-material/Psychology'; 
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'; 
import ContentCopyIcon from '@mui/icons-material/ContentCopy';   
import PostAddIcon from '@mui/icons-material/PostAdd'; 
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOffIcon from '@mui/icons-material/FolderOff';         

// Local Components
import PatientStatusDashboard from '../components/PatientStatusDashboard';
import MedicationReconciliationModal from '../components/MedicationReconciliationModal';
import PerpetualEpisodeBanner from '../components/PerpetualEpisodeBanner';
import ReadOnlySurveyRenderer from '../components/ReadOnlySurveyRenderer';
import UnifiedCareWizard from '../components/UnifiedCareWizard';
import EpisodeDetailModal from '../components/EpisodeDetailModal'; 
import ComplianceProgressCircle from '../components/ComplianceProgressCircle'; 
import ClinicianMiniCalendar from '../components/ClinicianMiniCalendar';
import MedicationsDashboard from '../components/MedicationsDashboard';
import SummariesDashboard from '../components/SummariesDashboard';
import VitalsDashboard from '../components/VitalsDashboard';
import MoodDashboard from '../components/MoodDashboard';
import KeyIndicatorsDashboard from '../components/KeyIndicatorsDashboard';
import InvestigationModal from '../components/InvestigationModal';
import DailyPlanManagerModal from '../components/DailyPlanManagerModal';

// Local Contexts & Utils
import { useAriaContext } from '../contexts/AriaContext';
import { useAuth } from '../contexts/AuthContext';
import authFetch from '../utils/authFetch';
import logger from '../utils/logger';
import jsPDF from 'jspdf';           
import html2canvas from 'html2canvas';

// --- Constants & Enums ---
const EpisodeStatus = { ACTIVE: "active", COMPLETED: "completed", CANCELLED: "cancelled", PENDING: "pending", PENDING_PATIENT_ACCEPTANCE: "pending_patient_acceptance", INVITED: "invited", SUSPENDED: "suspended", ARCHIVED: "archived" };
const EPISODE_TYPE_MAP = {
    'LIFETIME_PERPETUAL': { title: 'My Health Journal' },
    'ONBOARDING': { title: 'Pre-Encounter Care' },
    'TCM': { title: 'Transitional Care' },
    'RTM_MUSCULOSKELETAL': { title: 'RTM - Musculoskeletal' },
    'RTM_RESPIRATORY': { title: 'RTM - Respiratory' },
    'RTM': { title: 'Remote Therapeutic Monitoring' },
    'CCM': { title: 'Chronic Care Management' },
    'PCM': { title: 'Principal Care Management' },
    'BHI': { title: 'Behavioral Health Integration' },
    'DEFAULT': { title: 'Care Episode' },
};
// Options for the new status management dropdowns
const PATIENT_STATUS_OPTIONS = [
  { value: 'pre_surgical', label: 'Pre-Surgical' },
  { value: 'post_surgical', label: 'Post-Surgical' },
  { value: 'longitudinal', label: 'Longitudinal Care' },
];

// In a real application, this list would be fetched from an API
const PROCEDURE_TYPE_OPTIONS = [
  { value: '', label: 'None Assigned' },
  { value: 'cataract_surgery', label: 'Cataract Surgery' },
  { value: 'knee_replacement', label: 'Knee Replacement' },
  { value: 'spinal_fusion', label: 'Spinal Fusion' },
];
// --- React Query Keys ---
const patientDetailQueryKey = (patientUserId) => ['patient', patientUserId, 'detailV8'];
const patientEpisodesQueryKey = (patientUserId) => ['patient', patientUserId, 'episodesV8'];

// --- API Fetching Functions ---
const fetchPatientDetails = async ({ queryKey }) => {
    const [, patientUserId] = queryKey;
    if (!patientUserId) throw new Error("Patient User ID is required.");
    // [FIX] Use explicit /api/ prefix
    return authFetch(`/api/admin/users/${patientUserId}`);
};

const fetchAllPatientAssignments = (patientId) => authFetch(`/api/episodes/patient/${patientId}/assignments`);
const fetchPatientDirectives = (patientUserId) => authFetch(`/api/patients/${patientUserId}/directives`);
const fetchComplianceScoresAPI = (patientId) => {
    if (!patientId) return null;

    // Define the date range for the compliance calculation
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 6); // Set start date to 7 days ago

    // Helper to format dates as YYYY-MM-DD
    const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const params = new URLSearchParams({
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
    });

    // This API call now includes the required date parameters for 100% consistency
    return authFetch(`/api/patients/${patientId}/compliance-scores?${params.toString()}`);
};

const fetchPatientEpisodes = async ({ queryKey }) => {
    const [, patientUserId] = queryKey;
    if (!patientUserId) throw new Error("Patient User ID is required.");
    // This calls the clinician endpoint to list episodes
    // filtered by a specific patient_user_id
    return authFetch(`/api/episodes/?patient_user_id=${patientUserId}`);
};

// --- Helper Functions ---
const formatDisplayValue = (value, prefix = "") => {
    if (value === null || typeof value === 'undefined') return 'N/A';
    return prefix + String(value).replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const getStatusChipPropsV2 = (status) => {
    const s = status ? String(status).toUpperCase() : 'UNKNOWN';
    const props = { label: formatDisplayValue(s), size: "small" };
    if (s.includes('ACTIVE')) return { ...props, color: "success", variant: 'filled' };
    if (s.includes('PENDING') || s.includes('INVITED')) return { ...props, color: "warning", variant: 'outlined' };
    if (s.includes('CANCELLED') || s.includes('ARCHIVED')) return { ...props, color: "error", variant: 'filled' };
    if (s.includes('COMPLETED')) return { ...props, color: "primary", variant: 'filled' };
    return { ...props, color: "default", variant: 'outlined' };
};

// --- Main Component ---
function PatientDetail() {
    // Core Hooks
    const { patientUserId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const focusedEpisodeWorkspaceRef = useRef(null);
    const complianceModalRef = useRef(null);
    
    // Contexts & Local State
    const { user: loggedInUser, selectedTenant } = useAuth();
    const { setDeepPatientContext, setIsContextLoading, clearDeepPatientContext, deepPatientContext: deepPatientContextFromAria, isContextLoading } = useAriaContext();
    const [focusedEpisodeId, setFocusedEpisodeId] = useState(null);
    const [showHistoricalEpisodes, setShowHistoricalEpisodes] = useState(false);
    const [userHasInteractedWithEpisodes, setUserHasInteractedWithEpisodes] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // Modal & Wizard States
    const [wizardState, setWizardState] = useState({ open: false, pathway: null });
    const [surveyModalOpen, setSurveyModalOpen] = useState(false);
    const [currentSurveyData, setCurrentSurveyData] = useState(null);

    const [isMedRecModalOpen, setIsMedRecModalOpen] = useState(false);
    const [isEpisodeModalOpen, setIsEpisodeModalOpen] = useState(false); 
    const [selectedEpisodeForModal, setSelectedEpisodeForModal] = useState(null); 
    const [isComplianceModalOpen, setIsComplianceModalOpen] = useState(false);
    const [isDailyPlanModalOpen, setIsDailyPlanModalOpen] = useState(false);
    const [isCheckinModalOpen, setIsCheckinModalOpen] = useState(false);
    const [initialModalTab, setInitialModalTab] = useState(0);
    const [medsEndDate, setMedsEndDate] = useState(new Date());
    const [archiveConfirmation, setArchiveConfirmation] = useState({ open: false, episodeId: null, episodeTitle: '', userInput: '' });
    const [patientStatus, setPatientStatus] = useState('');
    const [procedureType, setProcedureType] = useState('');
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [mrnToLink, setMrnToLink] = useState('');
    const [linkError, setLinkError] = useState('');
    // --- Data Fetching (React Query) ---
    const { data: patientInfo, isLoading: loadingPatient, isError: isPatientError, error: patientError } = useQuery({
        queryKey: patientDetailQueryKey(patientUserId),
        queryFn: fetchPatientDetails,
        enabled: !!patientUserId,
    });

    const { data: allEpisodesData = [], isLoading: loadingEpisodes } = useQuery({
        queryKey: patientEpisodesQueryKey(patientUserId), 
        queryFn: fetchPatientEpisodes,
        enabled: !!patientUserId,
        staleTime: 1000 * 60, 
    });

    const { data: allMedDirectives = [] } = useQuery({
        queryKey: ['patientMedications', { patientUserId }],
        queryFn: () => fetchPatientDirectives(patientUserId),
        enabled: !!patientUserId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: allActionableAssignments = [], isLoading: isLoadingAssignments } = useQuery({
        queryKey: ['allPatientAssignments', patientUserId],
        queryFn: () => fetchAllPatientAssignments(patientUserId),
        enabled: !!patientUserId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: workspaceData, isLoading: isLoadingWorkspace } = useQuery({
        queryKey: ['patientWorkspace', patientUserId, medsEndDate.toISOString().split('T')[0]],
        queryFn: () => {
            const today = new Date(medsEndDate);
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            const endDateParam = `${year}-${month}-${day}`;
            // [FIX] Use explicit /api/ prefix
            return authFetch(`/api/patients/${patientUserId}/workspace-summary?endDate=${endDateParam}`);
        },
        enabled: !!patientUserId,
        keepPreviousData: true,
    });
    
    const { data: complianceScoresData } = useQuery({
        queryKey: ['patientComplianceScores', patientUserId],
        queryFn: () => fetchComplianceScoresAPI(patientUserId),
        enabled: !!patientUserId,
        refetchInterval: 30000, // Refresh every 30 seconds
    });

    useEffect(() => {
        if (patientInfo?.patient_profile) {
            setPatientStatus(patientInfo.patient_profile.patient_status || 'longitudinal');
            setProcedureType(patientInfo.patient_profile.procedure_type || '');
        }
    }, [patientInfo]);


    const updatePatientProfileMutation = useMutation({
        mutationFn: (updatePayload) => authFetch(`/api/patients/${patientUserId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updatePayload),
        }),
        onSuccess: () => {


            showSnackbar('Patient profile updated successfully.', 'success');
            queryClient.invalidateQueries({ queryKey: patientDetailQueryKey(patientUserId) });
        },
        onError: (error) => {
            showSnackbar(`Error updating profile: ${error.message}`, 'error');
        },
    });

    const handleProfileUpdate = () => {
        const payload = {
            patient_status: patientStatus,
            procedure_type: procedureType,
        };
        updatePatientProfileMutation.mutate(payload);

    };

    // --- ADD THIS BLOCK: Mutation and Handlers for linking patient to EHR ---
    const linkPatientMutation = useMutation({
        mutationFn: (identifierPayload) => authFetch(`/api/patients/${patientUserId}/link-ehr`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(identifierPayload),
        }),
        onSuccess: (data) => {
            showSnackbar('Patient successfully linked to EHR.', 'success');
            queryClient.invalidateQueries({ queryKey: patientDetailQueryKey(patientUserId) });
            queryClient.invalidateQueries({ queryKey: ['dashboardStats', selectedTenant?.tenant_id] });
            queryClient.invalidateQueries({ queryKey: ['accessiblePatientsAndSearch', selectedTenant?.tenant_id, ''] });
            setIsLinkModalOpen(false);
            setMrnToLink('');
            setLinkError('');
        },
        onError: (error) => {
            logger.error("EHR Link failed:", error);
            setLinkError(error.data?.detail || error.message || 'Failed to link patient.');
        },
    });

    const handleOpenLinkModal = () => {
        setMrnToLink(patientInfo?.patient_profile?.mrn || ''); // Pre-fill with MRN if it exists
        setLinkError('');
        setIsLinkModalOpen(true);
    };

    const handleSubmitLink = () => {
        if (!mrnToLink.trim()) {
            setLinkError('An MRN or patient identifier is required.');
            return;
        }
        
        // This 'system' URL is a placeholder from our tests.
        // In a real-world scenario, this would come from the tenant's configuration.
        const identifierPayload = {
            system: "http://hospital.smarthealthit.org",
            value: mrnToLink.trim()
        };
        
        linkPatientMutation.mutate(identifierPayload);
    };

    const [investigationModalState, setInvestigationModalState] = useState({
        open: false,
        taskType: null,
        taskId: null,
        title: '',
        data: null
    });

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);
    
    const updateEpisodeStatusMutation = useMutation({
        mutationFn: ({ episodeId, status }) => authFetch(`/api/episodes/${episodeId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: status }),
        }),
        onSuccess: () => {
            showSnackbar('Episode status updated successfully.', 'success');
            queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientUserId) });
            setFocusedEpisodeId(null);
        },
        onError: (error) => {
            const errorDetail = error.data?.detail || error.message || 'Unknown error';
            showSnackbar(`Error updating episode: ${errorDetail}`, 'error');
        },
    });

    const deleteEpisodeMutation = useMutation({
        mutationFn: (episodeId) => authFetch(`/api/episodes/${episodeId}`, { method: 'DELETE' }),
        onSuccess: (data, variables) => {
            showSnackbar('Episode successfully deleted.', 'info');
            queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientUserId) });
            setFocusedEpisodeId(currentFocusedId => (variables === currentFocusedId ? null : currentFocusedId));
        },
        onError: (error) => {
            const errorDetail = error.data?.detail || error.message || 'Unknown error';
            showSnackbar(`Error deleting episode: ${errorDetail}`, 'error');
        },
    });

    const archiveEpisodeMutation = useMutation({
        mutationFn: (episodeId) => authFetch(`/api/episodes/${episodeId}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'archived' }),
        }),
        onSuccess: () => {
            showSnackbar('Episode successfully archived and moved to history.', 'success');
            queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientUserId) });
        },
        onError: (error) => {
            const errorDetail = error.data?.detail || error.message || 'Unknown error';
            showSnackbar(`Error archiving episode: ${errorDetail}`, 'error');
        },
    });

    const handleMedWeekChange = (direction) => {
        setMedsEndDate(currentEndDate => {
            const newDate = new Date(currentEndDate);
            const dayAdjustment = direction === 'prev' ? -7 : 7;
            newDate.setDate(newDate.getDate() + dayAdjustment);
            
            if (newDate > new Date()) {
                return new Date();
            }
            return newDate;
        });
    };
    
    const isToday = (someDate) => {
        const today = new Date();
        return someDate.getDate() === today.getDate() &&
               someDate.getMonth() === today.getMonth() &&
               someDate.getFullYear() === today.getFullYear();
    };

    const handleAddMedicationToReport = useCallback((reportContent) => {
        logger.info("Request to add to report:", reportContent);
        showSnackbar("Content copied. Please paste into the report in the 'Manage' view.", "info");
        navigator.clipboard.writeText(reportContent);
    }, [showSnackbar]);

    const handleDeleteEpisode = (episodeId, episodeTitle) => {
        if (window.confirm(`Are you sure you want to permanently delete the episode "${episodeTitle}"? This action cannot be undone.`)) {
            deleteEpisodeMutation.mutate(episodeId);
        }
    };

    useEffect(() => {
        if (patientInfo?.id) {
            setIsContextLoading(true);
            const fetchDeepContext = async () => {
                try {
                    // [FIX] Use explicit /api/ prefix
                    const deepContextData = await authFetch(`/api/patients/${patientInfo.id}/deep_context`);
                    if (deepContextData && typeof deepContextData === 'object' && !deepContextData.error) {
                        setDeepPatientContext(deepContextData);
                    } else {
                        throw new Error(deepContextData?.error || "Failed to load a valid AI context.");
                    }
                } catch (fetchError) {
                    logger.error("Failed to fetch deep patient context", { error: fetchError.message });
                    setDeepPatientContext(null);
                    showSnackbar(`AI Context Error: ${fetchError.message || 'Unknown error'}.`, "error");
                } finally {
                    setIsContextLoading(false);
                }
            };
            fetchDeepContext();
        } else if (!loadingPatient) {
            clearDeepPatientContext();
        }
    }, [patientInfo?.id, loadingPatient, setIsContextLoading, setDeepPatientContext, clearDeepPatientContext, showSnackbar]);

    useEffect(() => {
        clearDeepPatientContext();
        setFocusedEpisodeId(null);
        return () => clearDeepPatientContext();
    }, [patientUserId, clearDeepPatientContext]);

    const { otherActiveEpisodes, historicalEpisodes, perpetualEpisode, draftEpisodes } = useMemo(() => {
        const activeStati = ["active", "invited", "suspended", "pending_patient_acceptance", "pending"];
        const historicalStati = ["completed", "cancelled", "archived"];
        const draftStati = [];

        const perpetualEp = allEpisodesData.find(ep => ep.episode_type === 'LIFETIME_PERPETUAL');
        const therapeuticEpisodes = allEpisodesData.filter(ep => ep.episode_type !== 'LIFETIME_PERPETUAL');
        
        // FIX: Case-insensitive status matching to ensure Knee/Scoliosis episodes are found
        const active = therapeuticEpisodes.filter(ep => activeStati.includes(String(ep.status).toLowerCase()));
        const historical = therapeuticEpisodes.filter(ep => historicalStati.includes(String(ep.status).toLowerCase()));
        const drafts = therapeuticEpisodes.filter(ep => draftStati.includes(String(ep.status).toLowerCase()));

        const result = {
            otherActiveEpisodes: active,
            historicalEpisodes: historical,
            perpetualEpisode: perpetualEp,
            draftEpisodes: drafts
        };
        return result;
    }, [allEpisodesData]);

    const { keyIndicators, generalSummaries } = useMemo(() => {
        // [FIX] Aggregate all distinct log types explicitly returned by the backend
        const allCheckIns = [
            ...(workspaceData?.assignments || []),
            ...(workspaceData?.symptom_logs || []),
            ...(workspaceData?.journal_logs || []),
            ...(workspaceData?.activity_logs || []),
            ...(workspaceData?.diet_gi_logs || [])
        ];
        
        // Expanded keywords to catch ALL types of logs in the "Key Indicators" dashboard
        const KEY_INDICATOR_TITLES = ['shortness of breath', 'bowel movement', 'pain', 'symptom', 'diet', 'activity', 'journal'];
        
        const indicators = allCheckIns.filter(ci => {
            const title = ci.item_name || ci.content_item?.title || "";
            return KEY_INDICATOR_TITLES.some(keyword => title.toLowerCase().includes(keyword));
        });
        
        const summaries = allCheckIns.filter(ci => {
            const title = ci.item_name || ci.content_item?.title || "";
            return !KEY_INDICATOR_TITLES.some(keyword => title.toLowerCase().includes(keyword));
        });
        
        return { keyIndicators: indicators, generalSummaries: summaries };
    }, [workspaceData]);


    const aiGlobalDisabled = isContextLoading || !deepPatientContextFromAria;
    const dobFormatted = patientInfo?.patient_profile?.date_of_birth ? format(parseISO(String(patientInfo.patient_profile.date_of_birth).split('T')[0]), 'PP') : 'N/A';

    const handleOpenEpisodeModal = () => {
        logger.info(`Opening Patient Workspace Modal for patientId: ${patientUserId}`);
        setIsEpisodeModalOpen(true);
    };

    const handleCloseEpisodeModal = () => {
        setIsEpisodeModalOpen(false);
        setSelectedEpisodeForModal(null);
        queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientUserId) });
    };

    const handleLaunchWizard = (pathway) => setWizardState({ open: true, pathway });
    const handleCloseWizard = (submitted) => {
        setWizardState({ open: false, pathway: null });
        if (submitted) {
            queryClient.invalidateQueries({ queryKey: patientEpisodesQueryKey(patientUserId) });
            queryClient.invalidateQueries({ queryKey: patientDetailQueryKey(patientUserId) });
        }
    };
    
    // --- START OF MODIFICATION ---
    // The targetEpisodeId now prefers the active therapeutic episode for new assignments.
    // This prevents new trackers from being tied to the lifetime episode.
    const targetEpisodeId = useMemo(() => {
        if (otherActiveEpisodes.length > 0) {
            return otherActiveEpisodes[0].id;
        }
        // FIX: Do NOT fallback to perpetualEpisode.id for this variable. 
        // If no active therapeutic episode exists, this should be null 
        // so the modal knows to enforce specific episode selection or show an error.
        return null;
    }, [otherActiveEpisodes]);
    
    // We also explicitly define the lifetime ID to pass down for "Standard Checkins"
    const lifetimeEpisodeId = useMemo(() => {
        return perpetualEpisode?.id || null;
    }, [perpetualEpisode]);
    // --- END OF MODIFICATION ---

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleOpenInvestigationModal = useCallback((task, dataPayload = null) => {
        if (!task) return;
        setInvestigationModalState({
            open: true,
            taskType: task.item_type || task.type,
            taskId: task.id,
            title: task.details?.item_name || task.content_item?.title || task.title || 'Investigation',
            data: dataPayload || task,
        });
    }, []);

    const handleCloseInvestigationModal = useCallback(() => {
        setInvestigationModalState({ open: false, taskType: null, taskId: null, title: '', data: null });
    }, []);

    const generateReportText = useCallback(() => {
        if (!workspaceData?.medications) return "No medication data to report.";
        const sortedMeds = [...workspaceData.medications].sort((a, b) => (a.item_name_detailed || '').localeCompare(b.item_name_detailed || ''));
        const overallCompliance = Math.round(workspaceData.overall_compliance?.meds?.['30_day'] ?? 0);
        let text = `Medication Adherence Summary (Last 30 Days)\n`;
        text += `Patient: ${patientInfo?.first_name || ''} ${patientInfo?.last_name || ''} (DOB: ${dobFormatted})\n`;
        text += `Overall Compliance: ${overallCompliance}%\n\n`;
        sortedMeds.forEach(med => {
            const medCompliance = Math.round(med.compliance_scores?.['30_day'] ?? 0);
            text += `- ${med.item_name_detailed}: 30-Day Compliance approx. ${medCompliance}%\n`;
        });
        return text;
    }, [workspaceData, patientInfo, dobFormatted]);
    
    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(generateReportText());
        showSnackbar("Adherence summary copied to clipboard.", "success");
    };
    
    const handleDownloadPdf = () => {
        const input = complianceModalRef.current;
        if (!input) return;
        html2canvas(input).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('l', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth - 20, pdfHeight - 20);
            pdf.save(`medication-adherence-${patientInfo?.last_name || 'patient'}-${new Date().toISOString().split('T')[0]}.pdf`);
        });
    };

    const renderEpisodeCard = (episode) => {
        if (!episode) return null;
        const prescriber = episode.primary_clinician || episode.created_by_user;
        const prescriberName = prescriber ? `${prescriber.first_name || ''} ${prescriber.last_name || ''}`.trim() : 'N/A';
        const episodeTypeInfo = EPISODE_TYPE_MAP[episode.episode_type?.toUpperCase()] || EPISODE_TYPE_MAP.DEFAULT;
        
        const programName = episode.program?.name || episodeTypeInfo.title;
        const isLifetime = episode.episode_type === 'LIFETIME_PERPETUAL';
        const hasBillingLogs = episode.billing_logs && episode.billing_logs.length > 0;
        const hasNonCancelledAssignments = episode.content_assignments && episode.content_assignments.some(a => a.status !== 'cancelled');
        const isDeletable = !hasBillingLogs && !hasNonCancelledAssignments && !isLifetime;
        const isActiveOrPending = [
            EpisodeStatus.ACTIVE, EpisodeStatus.PENDING, 
            EpisodeStatus.PENDING_PATIENT_ACCEPTANCE, EpisodeStatus.INVITED
        ].includes(episode.status) && !isLifetime;
        return (
            <Grid item key={episode.id} xs={12} md={6} lg={4}>


                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2.5 }}>                    
                    <Divider />
                    <CardActions sx={{ justifyContent: 'space-between', p:1.5, bgcolor: 'action.hover' }}>
                        <Button size="small" onClick={() => { setSelectedEpisodeForModal(episode); setIsEpisodeModalOpen(true); }} variant={"contained"} color="primary" endIcon={<LaunchIcon />}>{programName}</Button>
                        {isActiveOrPending && (
                            isDeletable ? ( 
                                <Tooltip title="Delete this empty episode">
                                    <span>
                                        <IconButton size="small" onClick={() => handleDeleteEpisode(episode.id, episodeTypeInfo.title)} disabled={deleteEpisodeMutation.isPending} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Archive this episode and move to history">
                                    <span>
                                        <IconButton size="small" onClick={() => setArchiveConfirmation({ open: true, episodeId: episode.id, episodeTitle: episodeTypeInfo.title, userInput: '' })} sx={{ color: 'action.active' }}>
                                            <FolderOffIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )
                        )}
                    </CardActions>
                </Card>
            </Grid>
        );
    };
    
    if (loadingPatient) return <Container sx={{ textAlign: 'center', mt: 4 }}><CircularProgress /><Typography sx={{mt:1}}>Loading Patient Data...</Typography></Container>;
    if (isPatientError) return <Container><Alert severity="error" icon={<ErrorOutlineIcon />}>Error Loading Patient: {patientError?.message || 'Unknown error'}</Alert></Container>;
    if (!patientInfo) return <Container><Button component={RouterLink} to="/patients" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>Back</Button><Alert severity="info" icon={<InfoOutlinedIcon />}>Patient data not found.</Alert></Container>;
    
    const reviewPriorityScore = 25;
    const healthPulseData = [ { week: 1, score: 10 }, { week: 2, score: 12 }, { week: 3, score: 9 }];

    return (
        <Container maxWidth="xl" sx={{ pt: {xs: 1, md: 2}, pb: 4, px: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Button component={RouterLink} to="/dashboard" startIcon={<ArrowBackIcon />} size="small">Back</Button>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" startIcon={<EventNoteIcon />} size="small" onClick={() => setIsDailyPlanModalOpen(true)}>
                        Manage Daily Plan
                    </Button>
                    <Button variant="outlined" startIcon={<MedicationIcon />} size="small" onClick={() => setIsMedRecModalOpen(true)}>
                        Medications
                    </Button>
                    <Button variant="contained" startIcon={<AddCircleOutlineIcon />} size="small" onClick={() => handleLaunchWizard('PrescribeTherapy')}>New Episode</Button>
                </Stack>
            </Box>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        EHR STATUS
                    </Typography>
                    {patientInfo?.patient_profile?.external_emr_id ? (
                        <Chip
                            icon={<CheckCircleOutlineIcon />}
                            label="Patient Linked"
                            color="success"
                            size="small"
                            sx={{ mt: 1, fontWeight: 'medium' }}
                        />
                    ) : (
                        <Chip
                            icon={<LinkIcon />}
                            label="Link to EHR Pending"
                            color="warning"
                            size="small"
                            sx={{ mt: 1, fontWeight: 'medium' }}
                        />
                    )}
                </Box>
                {!patientInfo?.patient_profile?.external_emr_id && (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleOpenLinkModal}
                        disabled={linkPatientMutation.isPending}
                    >
                        Link Patient
                    </Button>
                )}
            </Paper>

            <PatientStatusDashboard
                reviewPriorityScore={reviewPriorityScore}
                healthPulseData={healthPulseData}
                complianceData={complianceScoresData}
                onMedsClick={() => setIsComplianceModalOpen(true)}
                onCheckinsClick={() => setIsCheckinModalOpen(true)}
                onHealthPulseClick={handleOpenEpisodeModal}
            />

            {loadingEpisodes ? <Box sx={{display:'flex', justifyContent:'center', my:4}}><CircularProgress /></Box> : perpetualEpisode && (
               <PerpetualEpisodeBanner episode={perpetualEpisode} patientInfo={patientInfo} calendarData={workspaceData?.calendar_data || []} />
            )}
            
            <Box sx={{px: { xs: 1, sm: 0 }}}>
                <Box id="active-episodes-section" sx={{mb: 4, mt: 4}}>
                    <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}><EventNoteIcon sx={{mr:1, color: 'text.secondary'}}/> Active Therapy Programs</Typography>
                    {loadingEpisodes ? <Box sx={{display:'flex', justifyContent:'center', my:2}}><CircularProgress /></Box> :
                    (otherActiveEpisodes.length === 0) && (
                        <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
                            <Typography color="text.secondary">No active therapy programs.</Typography>
                        </Paper>
                    )}
                    <Grid container spacing={3}>{otherActiveEpisodes.map(episode => renderEpisodeCard(episode))}</Grid>
                </Box>
                {draftEpisodes.length > 0 && (
                    <Box id="draft-episodes-section" sx={{mb: 4, mt: 4}}>
                        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
                            <WarningAmberIcon sx={{mr:1, color: 'warning.main'}}/> Draft Plans (Not Ordered)
                        </Typography>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            These plans were started but never finalized and ordered. They are not active or visible to the patient. You can safely delete them.
                        </Alert>
                        <Grid container spacing={3}>{draftEpisodes.map(episode => renderEpisodeCard(episode))}</Grid>
                    </Box>
                )}

                <Button onClick={() => setShowHistoricalEpisodes(prev => !prev)} startIcon={<HistoryIcon />}>{showHistoricalEpisodes ? 'Hide' : 'Show'} Historical Episodes</Button>

                {showHistoricalEpisodes && (
                    <Box id="historical-episodes-section" sx={{mb: 4, mt: 2}}>
                        {loadingEpisodes && <Box sx={{display:'flex', justifyContent:'center', my:2}}><CircularProgress /></Box>}
                        <Grid container spacing={3}>{historicalEpisodes.map(episode => renderEpisodeCard(episode))}</Grid>
                    </Box>
                )}
            </Box>
            
            {wizardState.open && patientInfo && selectedTenant?.tenant_id && (
                <UnifiedCareWizard open={wizardState.open} onClose={handleCloseWizard} pathway={wizardState.pathway} tenantId={selectedTenant.tenant_id} initialPatientContext={patientInfo} />
            )}
            {isEpisodeModalOpen && (
                <EpisodeDetailModal open={isEpisodeModalOpen} onClose={handleCloseEpisodeModal} initialTab={initialModalTab} patientId={patientUserId} tenantId={selectedTenant?.tenant_id} patientDetails={patientInfo} allEpisodes={allEpisodesData} isLoadingEpisodes={loadingEpisodes} workspaceData={workspaceData} isLoadingWorkspace={isLoadingWorkspace} showSnackbar={showSnackbar} onOpenMedRec={() => setIsMedRecModalOpen(true)} onNewEpisode={() => handleLaunchWizard('PrescribeTherapy')} allMedDirectives={allMedDirectives} allActionableAssignments={allActionableAssignments} isLoadingAssignments={isLoadingAssignments} />
            )}
            {isMedRecModalOpen && (
                <MedicationReconciliationModal open={isMedRecModalOpen} onClose={() => setIsMedRecModalOpen(false)} patientUserId={patientUserId} episodeId={targetEpisodeId} patientName={`${patientInfo.first_name} ${patientInfo.last_name}`} showSnackbar={showSnackbar} tenantId={selectedTenant?.tenant_id} />
            )}

            {isDailyPlanModalOpen && (
                <DailyPlanManagerModal
                    open={isDailyPlanModalOpen}
                    onClose={() => setIsDailyPlanModalOpen(false)}
                    patientUserId={patientUserId}
                    patientName={`${patientInfo.first_name} ${patientInfo.last_name}`}
                    showSnackbar={showSnackbar}
                    episodeId={targetEpisodeId} 
                    lifetimeEpisodeId={lifetimeEpisodeId} 
                    activeEpisodes={otherActiveEpisodes} 
                />
            )}
            {investigationModalState.open && (
                <InvestigationModal open={investigationModalState.open} onClose={handleCloseInvestigationModal} patientId={patientUserId} {...investigationModalState} />
            )}
            <Dialog open={isComplianceModalOpen} onClose={() => setIsComplianceModalOpen(false)} fullWidth maxWidth="lg">
                <Box ref={complianceModalRef} sx={{ bgcolor: 'background.paper' }}>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '12px 24px' }}>
                        <Box>
                            <Typography variant="h6">Medication Adherence Log</Typography>
                            <Typography variant="body2" color="text.secondary">Patient: {patientInfo?.first_name} {patientInfo?.last_name} (DOB: {dobFormatted})</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title={`Overall 30-Day Compliance: ${(workspaceData?.overall_compliance?.meds?.['30_day'] ?? 0).toFixed(1)}%`}>
                                <Box><ComplianceProgressCircle value={workspaceData?.overall_compliance?.meds?.['30_day'] ?? 0} /></Box>
                            </Tooltip>
                            <Tooltip title="Add to Clinical Report"><IconButton size="small" onClick={() => handleAddMedicationToReport(generateReportText())}><PostAddIcon /></IconButton></Tooltip>
                            <Tooltip title="Copy Summary Text"><IconButton size="small" onClick={handleCopyToClipboard}><ContentCopyIcon /></IconButton></Tooltip>
                            <Tooltip title="Download as PDF"><IconButton size="small" onClick={handleDownloadPdf}><PictureAsPdfIcon /></IconButton></Tooltip>
                            <IconButton aria-label="close" onClick={() => setIsComplianceModalOpen(false)} sx={{ color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton>
                        </Stack>
                    </DialogTitle>
                    <DialogContent dividers sx={{ maxHeight: '70vh' }}>
                        {isLoadingWorkspace && !workspaceData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                        ) : workspaceData?.medications && workspaceData.medications.length > 0 ? (
                            <MedicationsDashboard medications={workspaceData.medications} onOpenModal={handleOpenInvestigationModal} onDateChange={handleMedWeekChange} currentEndDate={medsEndDate} isNextDisabled={isToday(medsEndDate)} isLoading={isLoadingWorkspace} />
                        ) : (
                            <Alert severity="info">No medication adherence data available for this patient.</Alert>
                        )}
                    </DialogContent>
                </Box>
                <DialogActions>
                    <Button onClick={() => setIsComplianceModalOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isCheckinModalOpen} onClose={() => setIsCheckinModalOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6">Daily Check-ins Summary</Typography>
                        <Typography variant="body2" color="text.secondary">Patient: {patientInfo?.first_name} {patientInfo?.last_name} (DOB: {dobFormatted})</Typography>
                    </Box>
                    <IconButton aria-label="close" onClick={() => setIsCheckinModalOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ maxHeight: '80vh', bgcolor: 'grey.50' }}>
                    {isLoadingWorkspace ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : workspaceData ? (
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            <ClinicianMiniCalendar calendarData={workspaceData.calendar_data} />
                            {workspaceData.vitals?.length > 0 && <VitalsDashboard vitals={workspaceData.vitals} onOpenModal={handleOpenInvestigationModal} />}
                            {/* [FIX] Render Subjective Metrics (Pain, Sleep, Energy) that were missing */}
                            {[
                                workspaceData?.pain, 
                                workspaceData?.energy, 
                                workspaceData?.sleep, 
                                workspaceData?.shortness_of_breath, 
                                workspaceData?.exertion
                            ].some(metric => metric?.series?.length > 0) && (
                                <VitalsDashboard 
                                    vitals={[
                                        workspaceData.pain,
                                        workspaceData.energy,
                                        workspaceData.sleep,
                                        workspaceData.shortness_of_breath,
                                        workspaceData.exertion
                                    ].filter(v => v && v.series && v.series.length > 0)}
                                    onOpenModal={handleOpenInvestigationModal}
                                    title="Subjective Metrics" 
                                />
                            )}

                            {/* [FIX] Render Activity Metrics */}
                            {(workspaceData?.activity_duration?.series?.length > 0 || workspaceData?.activity_difficulty?.series?.length > 0) && (
                                    <VitalsDashboard 
                                    vitals={[
                                        workspaceData.activity_duration,
                                        workspaceData.activity_difficulty
                                    ].filter(v => v && v.series && v.series.length > 0)}
                                    onOpenModal={handleOpenInvestigationModal}
                                    title="Activity Metrics"
                                />
                            )}
                            
                            {keyIndicators.length > 0 && <KeyIndicatorsDashboard checkIns={keyIndicators} onOpenModal={handleOpenInvestigationModal} />}
                            {workspaceData.mood?.series?.length > 0 && <MoodDashboard mood={workspaceData.mood} onOpenModal={handleOpenInvestigationModal} />}
                            {generalSummaries.length > 0 && <SummariesDashboard summaries={generalSummaries} onOpenModal={handleOpenInvestigationModal} />}
                        </Stack>
                    ) : (
                        <Alert severity="info">No check-in data available for this patient.</Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsCheckinModalOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>
            <Dialog
                open={archiveConfirmation.open}
                onClose={() => setArchiveConfirmation({ ...archiveConfirmation, open: false })}
                maxWidth="sm"
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Archive Episode</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        You are about to archive the episode: <strong>{archiveConfirmation.episodeTitle}</strong>.
                        The episode will be moved to historical records and hidden from the active dashboard.
                    </Alert>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        To proceed, please type **ARCHIVE** in the box below.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Type 'ARCHIVE' to confirm"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={archiveConfirmation.userInput}
                        onChange={(e) => setArchiveConfirmation({ ...archiveConfirmation, userInput: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setArchiveConfirmation({ ...archiveConfirmation, open: false })}>Cancel</Button>
                    <Button
                        onClick={() => {
                            archiveEpisodeMutation.mutate(archiveConfirmation.episodeId);
                            setArchiveConfirmation({ open: false, episodeId: null, episodeTitle: '', userInput: '' });
                        }}
                        color="error"
                        variant="contained"
                        disabled={archiveConfirmation.userInput !== 'ARCHIVE' || archiveEpisodeMutation.isPending}
                    >
                        {archiveEpisodeMutation.isPending ? 'Archiving...' : 'Confirm Archive'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={isLinkModalOpen} onClose={() => setIsLinkModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Link Patient to EHR
                    <IconButton onClick={() => setIsLinkModalOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography gutterBottom>
                        Search for this patient in the EHR using their Medical Record Number (MRN) or other unique identifier.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="normal"
                        label="Patient Identifier (e.g., MRN)"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={mrnToLink}
                        onChange={(e) => {
                            setMrnToLink(e.target.value);
                            setLinkError('');
                        }}
                        error={!!linkError}
                        helperText={linkError || "This will search the EHR and link the patient's records."}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setIsLinkModalOpen(false)} disabled={linkPatientMutation.isPending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitLink}
                        variant="contained"
                        disabled={linkPatientMutation.isPending}
                    >
                        {linkPatientMutation.isPending ? <CircularProgress size={24} /> : "Find & Link Patient"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default PatientDetail;